import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';

import { CHILDREN_PROP_TYPES } from 'propTypes';
import AppBarContainer from 'bento-ordering/base/containers/AppBarContainer';
import Box from '@mui/material/Box';
import Footer from 'components/Footer';
import { isMobileMedia } from 'bento-ordering/utils/media';
import MenuPageFooter from 'bento-ordering/menus/containers/MenuPageFooter';
import GridContainer from 'bento-ordering/base/ui_components/GridContainer';
import Hero from 'components/Hero';
import SecondaryCta from 'components/Buttons/SecondaryCta';
import {
  DEFAULT_LAYOUT_PADDING,
  MENU_LAYOUT_PADDING
} from 'styles/constants/layoutPadding';
import MenuSectionsBar from 'bento-ordering/menus/containers/MenuSectionsBar';
import LocationsCustomMessage from 'bento-ordering/locations/components/LocationsCustomMessage';
import { isSoleEntity } from 'bento-ordering/base/reducers/bentoApi';
import { getCustomMessageSelector } from 'bento-ordering/account/reducers/index';
import { MAX_MENU_WIDTH } from 'bento-ordering/base/constants/maxWidth';
import {
  getButtonBackgroundHoverColor,
  getItemDescriptionsAndPricesTypography
} from 'styles/helpers/getThemeStyles';

class BaseLayout extends React.PureComponent {
  static propTypes = {
    account: PropTypes.object,
    children: CHILDREN_PROP_TYPES.isRequired,
    mobileFooter: CHILDREN_PROP_TYPES,
    hideHeroOnMobile: PropTypes.bool,
    hideNav: PropTypes.bool,
    hideBrandFooterOnMobile: PropTypes.bool,
    hideMobileStickyCtaFooter: PropTypes.bool,
    heroImage: PropTypes.object,
    layoutPadding: PropTypes.object,
    isModal: PropTypes.bool,
    isFetching: PropTypes.bool,
    utmFooterLink: PropTypes.string,
    heroContent: CHILDREN_PROP_TYPES,
    id: PropTypes.string,
    shouldUseMenuStyle: PropTypes.bool,
    isSingleLocation: PropTypes.bool,
    customMessage: PropTypes.string
  };

  static defaultProps = {
    mobileFooter: <MenuPageFooter />,
    layoutPadding: DEFAULT_LAYOUT_PADDING,
    shouldUseMenuStyle: false
  };

  renderNav = () => {
    if (this.props.hideNav) {
      return null;
    }
    return <AppBarContainer />;
  };

  renderMobileFooter = () => {
    if (this.props.hideMobileStickyCtaFooter || this.props.isFetching) {
      return null;
    }

    return this.props.mobileFooter;
  };

  renderHero() {
    const { hideHeroOnMobile, heroImage, heroContent } = this.props;

    const shouldDisplayOnMobile = !(isMobileMedia() && hideHeroOnMobile);
    const noHeroContent = !!(heroImage?.url || heroContent);

    // couple of cases where we don't display hero
    // 1) if current viewport is mobile, and the prop has been set to hide on mobile
    // 2) if there is no hero content, which can include an image, or other specified
    // hero content (like a dom node containing a search bar on location list page)
    const shouldDisplayHero = shouldDisplayOnMobile && noHeroContent;

    if (shouldDisplayHero) {
      return <Hero image={heroImage}>{heroContent}</Hero>;
    }
    return null;
  }

  renderMenuSectionsBar() {
    const { shouldUseMenuStyle } = this.props;

    if (shouldUseMenuStyle) {
      return <MenuSectionsBar />;
    }
    return null;
  }

  /**
   * render the menu container custom text
   */
  renderMenuCustomText() {
    const { shouldUseMenuStyle, customMessage, isSingleLocation } = this.props;

    if (shouldUseMenuStyle) {
      if (customMessage && isSingleLocation) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                width: MAX_MENU_WIDTH,
                borderBottom: '1px solid #E5E5E6'
              }}
            >
              <Box
                textAlign="center"
                pt={3}
                ml={2}
                mr={2}
                sx={{
                  maxWidth: MAX_MENU_WIDTH
                }}
              >
                <LocationsCustomMessage message={customMessage} />
              </Box>
            </Box>
          </Box>
        );
      }
    }
  }

  render() {
    const {
      layoutPadding,
      isModal,
      isFetching,
      hideBrandFooterOnMobile,
      children,
      utmFooterLink,
      id,
      shouldUseMenuStyle
    } = this.props;

    const padding = shouldUseMenuStyle ? MENU_LAYOUT_PADDING : layoutPadding;

    const ownFooterState = theme => {
      return {
        color: getButtonBackgroundHoverColor(theme),
        ...getItemDescriptionsAndPricesTypography(theme)
      };
    };
    return (
      <Box pt={isModal ? {} : { xs: 7, sm: 8 }}>
        {this.renderNav()}
        {this.renderHero()}
        {this.renderMenuCustomText()}
        {this.renderMenuSectionsBar()}
        <GridContainer {...padding}>
          <div id={id}>{children}</div>

          {!isFetching && isFetching !== undefined && (
            <Footer hideBrandFooterOnMobile={hideBrandFooterOnMobile}>
              <SecondaryCta
                label="Powered By Bentobox"
                component="a"
                href={utmFooterLink}
                dataTestId="footer"
                ownState={{
                  footer: ownFooterState
                }}
              />
            </Footer>
          )}
          {this.renderMobileFooter()}
        </GridContainer>
      </Box>
    );
  }
}

const mapStateToProps = state => {
  const utmFooterLink = get(state.account, 'utm_footer_link', '');
  const account = state.account;
  const restaurantLocations = state.restaurantLocations;

  return {
    utmFooterLink,
    customMessage: getCustomMessageSelector(account),
    isSingleLocation: isSoleEntity(restaurantLocations),
    account
  };
};

export default connect(mapStateToProps)(BaseLayout);
